import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { useQuery, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { z } from "zod"
import { fixtureTypeArticlesQueryOptions } from "@/features/bill-of-materials/hooks/useFixtureTypeArticles"
import { fixtureTypeShelfCodesQueryOptions } from "@/features/bill-of-materials/hooks/useFixtureTypeShelfCodes"
import { lomExtrasQueryOptions } from "@/features/bill-of-materials/hooks/useLomExtras"
import { shelfCodesQueryOptions } from "@/features/bill-of-materials/hooks/useShelfCodes"
import { deleteFixtureTypeArticleFn } from "@/features/bill-of-materials/server/fixtureTypeArticles"
import { deleteFixtureTypeShelfCodeFn } from "@/features/bill-of-materials/server/fixtureTypeShelfCodes"
import {
  deleteLomExtraFn,
  deleteLomExtraQuantityRuleFn,
} from "@/features/bill-of-materials/server/lomExtras"
import {
  type Extra,
  ExtraFormPanel,
} from "@/features/list-of-materials/components/extras/ExtraFormPanel"
import {
  type ExtraRule,
  ExtraRuleFormPanel,
} from "@/features/list-of-materials/components/extras/ExtraRuleFormPanel"
import { ExtrasPerStore } from "@/features/list-of-materials/components/extras/ExtrasPerStore"
import {
  type FixtureArticle,
  FixtureArticleFormPanel,
} from "@/features/list-of-materials/components/extras/FixtureArticleFormPanel"
import { FixtureArticles } from "@/features/list-of-materials/components/extras/FixtureArticles"
import {
  type FixtureShelfCodeLink,
  LinkShelfCodePanel,
} from "@/features/list-of-materials/components/extras/LinkShelfCodePanel"
import { ConfirmDialog } from "@/features/list-of-materials/components/form/ConfirmDialog"
import { HowLink } from "@/features/list-of-materials/components/how-it-works/HowLink"
import { Button } from "@/features/list-of-materials/components/ui/Button"
import { PageHead } from "@/features/list-of-materials/components/ui/PageHead"
import { TabLink, Tabs } from "@/features/list-of-materials/components/ui/Tabs"
import { fixtureLabel } from "@/features/list-of-materials/core/fixtures"
import { useCanEditLom } from "@/features/list-of-materials/hooks/useCanEditLom"
import {
  lomCountriesQueryOptions,
  lomExtraRulesQueryOptions,
  lomStoreSizesQueryOptions,
  pickableArticlesQueryOptions,
} from "@/features/list-of-materials/hooks/useLomData"
import { useLomMutation } from "@/features/list-of-materials/hooks/useLomMutation"
import {
  lomVersionsQueryOptions,
  useSelectedLomVersion,
} from "@/features/list-of-materials/hooks/useLomVersion"

export const Route = createFileRoute("/(authenticated)/list-of-materials/extras")({
  validateSearch: z.object({ view: z.enum(["store", "fixture"]).optional().catch(undefined) }),
  loaderDeps: ({ search }) => ({ version: search.version }),
  loader: async ({ context, deps }) => {
    const { selected } = await context.queryClient.ensureQueryData(
      lomVersionsQueryOptions(deps.version),
    )
    if (!selected) return
    await Promise.all([
      context.queryClient.ensureQueryData(lomExtrasQueryOptions(selected.id)),
      context.queryClient.ensureQueryData(lomExtraRulesQueryOptions(selected.id)),
      context.queryClient.ensureQueryData(fixtureTypeArticlesQueryOptions(selected.id)),
      context.queryClient.ensureQueryData(fixtureTypeShelfCodesQueryOptions(selected.id)),
    ])
  },
  component: ExtrasPage,
})

type Removal =
  | { kind: "extra"; extra: Extra }
  | { kind: "rule"; rule: ExtraRule }
  | { kind: "fixture"; article: FixtureArticle }
  | { kind: "link"; link: FixtureShelfCodeLink }

function ExtrasPage() {
  const { version, versionSearch } = useSelectedLomVersion()
  const { view = "store" } = Route.useSearch()
  const canEdit = useCanEditLom()
  const { data: extras } = useSuspenseQuery(lomExtrasQueryOptions(version.id))
  const { data: rules } = useSuspenseQuery(lomExtraRulesQueryOptions(version.id))
  const { data: fixtureArticles } = useSuspenseQuery(fixtureTypeArticlesQueryOptions(version.id))
  const { data: links } = useSuspenseQuery(fixtureTypeShelfCodesQueryOptions(version.id))
  const { data: shelfCodes = [] } = useQuery({
    ...shelfCodesQueryOptions(version.id),
    enabled: canEdit,
  })
  const { data: countries = [] } = useQuery(lomCountriesQueryOptions)
  const { data: storeSizes = [] } = useQuery(lomStoreSizesQueryOptions)
  const { data: articles = [] } = useQuery({
    ...pickableArticlesQueryOptions(version.id),
    enabled: canEdit,
  })

  const [extraPanel, setExtraPanel] = useState<{ extra?: Extra } | null>(null)
  const [rulePanel, setRulePanel] = useState<{ extra: Extra; rule?: ExtraRule } | null>(null)
  const [fixturePanel, setFixturePanel] = useState<{
    article?: FixtureArticle
    shelveType?: string
  } | null>(null)
  const [linking, setLinking] = useState<{
    shelveType?: string
    link?: FixtureShelfCodeLink
  } | null>(null)
  const [removing, setRemoving] = useState<Removal | null>(null)
  const countryName = (id: string) => countries.find((c) => c.id === id)?.name

  const remove = useLomMutation(
    (r: Removal) =>
      r.kind === "extra"
        ? deleteLomExtraFn({ data: { id: r.extra.id } })
        : r.kind === "rule"
          ? deleteLomExtraQuantityRuleFn({ data: { id: r.rule.id } })
          : r.kind === "link"
            ? deleteFixtureTypeShelfCodeFn({ data: { id: r.link.id } })
            : deleteFixtureTypeArticleFn({ data: { id: r.article.id } }),
    { success: t`Deleted`, onSuccess: () => setRemoving(null) },
  )

  const tab = (value: "store" | "fixture", label: string, count: number) => (
    <TabLink
      to="/list-of-materials/extras"
      search={{ ...versionSearch, view: value }}
      current={view === value}
      count={count}
    >
      {label}
    </TabLink>
  )

  return (
    <>
      <PageHead
        help={<HowLink topic="extras" />}
        title={<Trans>Extras</Trans>}
        sub={
          <Trans>
            What a store needs besides its racking: per store, following its blueprint or store
            information, or for every fixture of a kind on its blueprint.
          </Trans>
        }
        right={
          canEdit && (
            <div className="flex gap-2">
              {view === "fixture" && (
                <Button tone="secondary" onClick={() => setLinking({})}>
                  <Trans>Link a shelf code</Trans>
                </Button>
              )}
              <Button
                tone="primary"
                onClick={() => (view === "store" ? setExtraPanel({}) : setFixturePanel({}))}
              >
                {view === "store" ? (
                  <Trans>New extra per store</Trans>
                ) : (
                  <Trans>Add fixture article</Trans>
                )}
              </Button>
            </div>
          )
        }
      />
      <Tabs>
        {tab("store", t`Per store`, extras.length)}
        {tab("fixture", t`Per fixture`, fixtureArticles.length)}
      </Tabs>

      {view === "store" ? (
        <ExtrasPerStore
          extras={extras}
          rules={rules}
          countryName={countryName}
          onEdit={canEdit ? (extra) => setExtraPanel({ extra }) : undefined}
          onDelete={canEdit ? (extra) => setRemoving({ kind: "extra", extra }) : undefined}
          onAddRule={canEdit ? (extra) => setRulePanel({ extra }) : undefined}
          onEditRule={canEdit ? (extra, rule) => setRulePanel({ extra, rule }) : undefined}
          onDeleteRule={canEdit ? (rule) => setRemoving({ kind: "rule", rule }) : undefined}
        />
      ) : (
        <FixtureArticles
          articles={fixtureArticles}
          countryName={(id) => countries.find((c) => c.id === id)?.name ?? id}
          onAdd={canEdit ? (shelveType) => setFixturePanel({ shelveType }) : undefined}
          onEdit={canEdit ? (article) => setFixturePanel({ article }) : undefined}
          onDelete={canEdit ? (article) => setRemoving({ kind: "fixture", article }) : undefined}
          links={links}
          onLink={canEdit ? (shelveType) => setLinking({ shelveType }) : undefined}
          onUnlink={canEdit ? (link) => setRemoving({ kind: "link", link }) : undefined}
          onEditLink={canEdit ? (link) => setLinking({ link }) : undefined}
        />
      )}

      <ExtraFormPanel
        open={extraPanel !== null}
        onOpenChange={(open) => !open && setExtraPanel(null)}
        configVersionId={version.id}
        extra={extraPanel?.extra}
        existingArticles={extras.map((e) => e.articleNumber)}
        articles={articles}
      />
      <ExtraRuleFormPanel
        target={rulePanel}
        onClose={() => setRulePanel(null)}
        siblings={rules.filter((r) => r.lomExtraId === rulePanel?.extra.id)}
        countries={countries}
        storeSizes={storeSizes}
      />
      <FixtureArticleFormPanel
        target={fixturePanel}
        onClose={() => setFixturePanel(null)}
        configVersionId={version.id}
        existing={fixtureArticles}
        articles={articles}
        countries={countries}
        storeSizes={storeSizes}
      />
      <LinkShelfCodePanel
        target={linking}
        onClose={() => setLinking(null)}
        configVersionId={version.id}
        shelfCodes={shelfCodes}
        links={links}
        countries={countries}
        storeSizes={storeSizes}
      />
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={
          removing?.kind === "extra"
            ? t`Delete extra ${removing.extra.articleNumber}?`
            : removing?.kind === "rule"
              ? t`Delete this amount?`
              : removing?.kind === "link"
                ? t`Unlink ${removing.link.shelfCode} from ${fixtureLabel(removing.link.shelveType)}?`
                : t`Delete ${removing?.article.articleNumber ?? ""} from ${fixtureLabel(removing?.article.shelveType ?? "")}?`
        }
        confirmLabel={removing?.kind === "link" ? t`Unlink` : t`Delete`}
        pending={remove.isPending}
        error={remove.error}
        onConfirm={() => removing && remove.mutate(removing)}
      >
        <p>
          {removing?.kind === "extra"
            ? t`No store gets ${removing.extra.description} any more; its other amounts go with it.`
            : removing?.kind === "rule"
              ? t`These stores get the standard amount again.`
              : removing?.kind === "link"
                ? t`These fixtures no longer get the parts of ${removing.link.shelfCode}.`
                : t`Stores no longer get this article for these fixtures.`}
        </p>
      </ConfirmDialog>
    </>
  )
}
